// The service's booking window — horizon, notice and closed days — from
// get_service_booking_window. One cached read per service, shared by every
// panel that asks: BookingSetup's notice strip and date pickers (moved here
// from BookingSetup.tsx, where one mount read it twice, once uncached) and the
// availability panel's day strip.
//
// Cached on the PROMISE so concurrent mounts share one in-flight call. A failed
// lookup is evicted rather than cached: the old module cache kept a
// null-resolving promise for the life of the page, so one dropped request hid
// the horizon and the closed days until a reload. Callers still treat a failure
// as "no window" — the server gates refuse a closed day, a too-far date and a
// too-soon start at hold time, so no answer can ever let a bad booking through.

import { useEffect, useState } from "react";
import { supabase } from "../config";

export type BookingWindowClosure = {
  starts_on?: string;
  ends_on?: string;
  guest_message?: string | null;
};

/** The RPC's own shape (every key optional: older deployments omit some). */
export type ServiceBookingWindowRow = {
  max_advance_days?: number | null;
  min_lead_hours?: number | null;
  /** availability_rules kind min_lead_minutes (20260913090030). */
  min_lead_minutes?: number | null;
  closed_dates?: string[] | null;
  closures?: BookingWindowClosure[] | null;
};

const cache = new Map<string, Promise<ServiceBookingWindowRow | null>>();

export function fetchServiceBookingWindow(
  serviceId: string,
): Promise<ServiceBookingWindowRow | null> {
  const hit = cache.get(serviceId);
  if (hit) return hit;
  const evict = () => {
    if (cache.get(serviceId) === p) cache.delete(serviceId);
  };
  // Promise.resolve because the supabase builder is a thenable, not a Promise —
  // it has no .catch, and a rejection here would surface as an unhandled one.
  const p: Promise<ServiceBookingWindowRow | null> = Promise.resolve(
    supabase.rpc("get_service_booking_window", { p_service_id: serviceId }),
  ).then(
    ({ data, error }: { data: unknown; error: unknown }) => {
      if (error || !data || typeof data !== "object") {
        evict();
        return null;
      }
      return data as ServiceBookingWindowRow;
    },
    () => {
      evict();
      return null;
    },
  );
  cache.set(serviceId, p);
  return p;
}

export type ServiceBookingWindow = {
  /** True once the read settled (answered or failed). */
  loaded: boolean;
  /** Raw max_advance_days (null = none). 0 means "today only" to the server. */
  maxAdvanceDays: number | null;
  minLeadHours: number | null;
  minLeadMinutes: number | null;
  /** Read-only by convention: shared by every reader of this service. */
  closedDates: Set<string>;
  closures: BookingWindowClosure[];
};

const EMPTY: ServiceBookingWindow = {
  loaded: false,
  maxAdvanceDays: null,
  minLeadHours: null,
  minLeadMinutes: null,
  closedDates: new Set<string>(),
  closures: [],
};

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

export function readServiceBookingWindow(
  row: ServiceBookingWindowRow | null,
): ServiceBookingWindow {
  return {
    loaded: true,
    maxAdvanceDays: num(row?.max_advance_days),
    minLeadHours: num(row?.min_lead_hours),
    minLeadMinutes: num(row?.min_lead_minutes),
    closedDates: Array.isArray(row?.closed_dates)
      ? new Set(row.closed_dates.filter((d): d is string => typeof d === "string"))
      : new Set<string>(),
    closures: Array.isArray(row?.closures) ? row.closures : [],
  };
}

export function useServiceBookingWindow(
  serviceId: string | null | undefined,
): ServiceBookingWindow {
  const [w, setW] = useState<{ id: string; value: ServiceBookingWindow } | null>(null);
  useEffect(() => {
    if (!serviceId) return;
    let alive = true;
    void fetchServiceBookingWindow(serviceId).then((row) => {
      if (alive) setW({ id: serviceId, value: readServiceBookingWindow(row) });
    });
    return () => {
      alive = false;
    };
  }, [serviceId]);
  // An answer for a previous service is never this service's window.
  return w && w.id === serviceId ? w.value : EMPTY;
}
